import React, { useState } from "react";
import { toast } from "sonner";
import { Ban, CheckCircle2, Loader2 } from "lucide-react";
import { facilitiesApi } from "../../../lib/api";
import { TID } from "../../../constants/testIds";
import { getApiError } from "../../../lib/utils";

export const StatusModal = ({ mode, facility, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const isSuspend = mode === "suspend";

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Please provide a reason (min 3 chars)");
      return;
    }
    setLoading(true);
    try {
      if (isSuspend) await facilitiesApi.suspend(facility.facility_id, reason.trim());
      else await facilitiesApi.reactivate(facility.facility_id, reason.trim());
      toast.success(`${facility.name} ${isSuspend ? "suspended" : "reactivated"}`);
      onDone();
    } catch (e) {
      toast.error(getApiError(e, "Action failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-md border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            {isSuspend ? <Ban size={18} className="text-red-600" strokeWidth={2.5} /> : <CheckCircle2 size={18} className="text-emerald-600" strokeWidth={2.5} />}
            <h3 className="font-heading text-base font-bold text-slate-900">
              {isSuspend ? "Suspend facility" : "Reactivate facility"}
            </h3>
          </div>
          <p className="mt-1 text-xs text-slate-600">{facility.name} · <span className="font-mono">{facility.facility_id}</span></p>
        </div>
        <div className="p-5">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Reason (required · min 3 chars)
          </label>
          <textarea
            data-testid={TID.reasonInput}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            className="w-full resize-none rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
            placeholder={isSuspend ? "e.g. Failed re-accreditation audit" : "e.g. Compliance restored"}
          />
          <p className="mt-2 text-[11px] text-slate-500">This will be broadcast to all approved users and permanently logged.</p>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button data-testid={TID.cancelModal} onClick={onClose} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            data-testid={isSuspend ? TID.confirmSuspend : TID.confirmReactivate}
            onClick={submit}
            disabled={loading}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-60 ${
              isSuspend ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {loading && <Loader2 size={12} className="animate-spin" />}
            {isSuspend ? "Confirm suspend" : "Confirm reactivate"}
          </button>
        </div>
      </div>
    </div>
  );
};
